/**
 * TASK-COMBAT-VIZ-001: CombatSocketClient Implementation
 *
 * Manages Socket.IO connection for real-time combat events.
 * Handles connection lifecycle, room management, and event routing.
 */

import { io, Socket } from 'socket.io-client';
import { getAuth } from 'firebase/auth';
import type { CombatState, CombatResult, CombatEvent, VisualCombatAttribute } from '@drawn-of-war/shared/types/combat';
import { StateDeltaDecoder } from './state-delta-decoder';
import type { CombatBroadcast } from './state-delta-decoder';

/**
 * Data payload for effectFrames event (TASK-EFD-003)
 */
export interface EffectFramesData {
  matchId: string;
  frames: Record<string, VisualCombatAttribute[]>;
}

/**
 * Connection status enum
 */
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

/**
 * Client options
 */
export interface CombatSocketClientOptions {
  autoConnect?: boolean;
  reconnect?: boolean;
}

/**
 * CombatSocketClient
 *
 * Manages Socket.IO connection to /combat namespace for real-time combat updates.
 */
export class CombatSocketClient {
  private socket: Socket | null = null;
  private serverUrl: string;
  private matchId: string;
  private options: Required<CombatSocketClientOptions>;
  private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private manualDisconnect: boolean = false;

  // Event callbacks
  private statusChangeCallbacks: ((status: ConnectionStatus) => void)[] = [];
  private combatStateCallbacks: ((state: CombatState) => void)[] = [];
  private combatEventsCallbacks: ((events: CombatEvent[]) => void)[] = [];
  private combatCompletedCallbacks: ((result: CombatResult) => void)[] = [];
  private errorCallbacks: ((error: Error) => void)[] = [];
  // TASK-EFD-003: EffectFrames reliable delivery callbacks
  private effectFramesCallbacks: ((data: EffectFramesData) => void)[] = [];
  // TASK-EFD-FIX: Buffer effectFrames received before callbacks are registered
  private bufferedEffectFrames: EffectFramesData | null = null;
  // Delta decoder for USE_DELTA_ENCODING broadcasts
  private deltaDecoder: StateDeltaDecoder = new StateDeltaDecoder();

  constructor(serverUrl: string, matchId: string, options: CombatSocketClientOptions = {}) {
    // Validate matchId
    if (!matchId || matchId.trim() === '') {
      throw new Error('Match ID is required');
    }

    this.serverUrl = serverUrl;
    this.matchId = matchId;
    this.options = {
      autoConnect: options.autoConnect ?? false,
      reconnect: options.reconnect ?? true
    };

    // NOTE: Socket is NOT created here - it's created in connect() after fetching auth token
    // This ensures the auth token is available when the socket connection is established

    // Auto-connect if enabled
    if (this.options.autoConnect) {
      this.connect();
    }
  }

  /**
   * Connect to combat socket
   * Fetches Firebase auth token and creates socket connection
   */
  public async connect(): Promise<void> {
    if (this.socket?.connected) {
      return; // Already connected
    }

    // Update status
    this.updateStatus(ConnectionStatus.CONNECTING);
    this.manualDisconnect = false;

    // Get Firebase auth token
    let authToken: string | undefined;
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user) {
        authToken = await user.getIdToken();
        console.log('[CombatSocketClient] Got Firebase ID token for user:', user.uid);
      } else {
        console.warn('[CombatSocketClient] No authenticated user found');
      }
    } catch (error) {
      console.error('[CombatSocketClient] Failed to get auth token:', error);
    }

    // Create socket with auth token if it doesn't exist
    if (!this.socket) {
      console.log('[CombatSocketClient] Creating socket connection to:', `${this.serverUrl}/combat`);
      this.socket = io(`${this.serverUrl}/combat`, {
        autoConnect: false,
        reconnection: this.options.reconnect,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 30,
        auth: {
          token: authToken || '' // Pass Firebase ID token for authentication
        }
      });

      // Setup event handlers after creating socket
      this.setupSocketHandlers();
    }

    // Connect
    this.socket.connect();
  }

  /**
   * Disconnect from combat socket
   */
  public disconnect(): void {
    if (!this.socket) {
      return;
    }

    // Mark as manual disconnect to prevent auto-reconnect
    this.manualDisconnect = true;

    // Leave match room before disconnecting
    if (this.socket.connected) {
      this.socket.emit('combat:leave', this.matchId);
    }

    // Cleanup listeners
    this.cleanupSocketHandlers();

    // Reset delta decoder state
    this.deltaDecoder.reset();

    // Disconnect
    this.socket.disconnect();
    this.socket = null;

    // Update status
    this.updateStatus(ConnectionStatus.DISCONNECTED);
  }

  /**
   * Signal that client is ready to receive combat state
   * Should be called after texture preloading is complete
   */
  public signalReady(): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('[CombatSocketClient] Cannot signal ready - not connected');
      return;
    }

    console.log('[CombatSocketClient] Signaling client ready for match:', this.matchId);
    this.socket.emit('combat:ready', this.matchId);
  }

  /**
   * Get current connection status
   */
  public getConnectionStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * Register status change callback
   */
  public onStatusChange(callback: (status: ConnectionStatus) => void): void {
    this.statusChangeCallbacks.push(callback);
  }

  /**
   * Register combat state callback
   */
  public onCombatState(callback: (state: CombatState) => void): void {
    this.combatStateCallbacks.push(callback);
  }

  /**
   * Register combat events callback
   */
  public onCombatEvents(callback: (events: CombatEvent[]) => void): void {
    this.combatEventsCallbacks.push(callback);
  }

  /**
   * Register combat completed callback
   */
  public onCombatCompleted(callback: (result: CombatResult) => void): void {
    this.combatCompletedCallbacks.push(callback);
  }

  /**
   * Register error callback
   */
  public onError(callback: (error: Error) => void): void {
    this.errorCallbacks.push(callback);
  }

  // =====================================================================
  // CALLBACK REMOVAL METHODS
  // =====================================================================

  /**
   * Remove a status change callback
   */
  public offStatusChange(callback: (status: ConnectionStatus) => void): void {
    this.statusChangeCallbacks = this.statusChangeCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove a combat state callback
   */
  public offCombatState(callback: (state: CombatState) => void): void {
    this.combatStateCallbacks = this.combatStateCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove a combat events callback
   */
  public offCombatEvents(callback: (events: CombatEvent[]) => void): void {
    this.combatEventsCallbacks = this.combatEventsCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove a combat completed callback
   */
  public offCombatCompleted(callback: (result: CombatResult) => void): void {
    this.combatCompletedCallbacks = this.combatCompletedCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove an error callback
   */
  public offError(callback: (error: Error) => void): void {
    this.errorCallbacks = this.errorCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove an effectFrames callback
   */
  public offEffectFrames(callback: (data: EffectFramesData) => void): void {
    this.effectFramesCallbacks = this.effectFramesCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Remove all callbacks of all types.
   * Useful for full cleanup on unmount.
   */
  public removeAllCallbacks(): void {
    this.statusChangeCallbacks = [];
    this.combatStateCallbacks = [];
    this.combatEventsCallbacks = [];
    this.combatCompletedCallbacks = [];
    this.errorCallbacks = [];
    this.effectFramesCallbacks = [];
  }

  /**
   * TASK-EFD-003: Register effectFrames callback for reliable delivery
   */
  public onEffectFrames(callback: (data: EffectFramesData) => void): void {
    this.effectFramesCallbacks.push(callback);

    // TASK-EFD-FIX: If we have buffered effectFrames, replay them to the new callback
    if (this.bufferedEffectFrames) {
      console.log(`[CombatSocketClient] Replaying buffered effectFrames to new callback (${Object.keys(this.bufferedEffectFrames.frames).length} units)`);
      callback(this.bufferedEffectFrames);
    }
  }

  /**
   * TASK-EFD-004: Request missing effectFrames from server
   */
  public requestEffectFrames(): void {
    if (!this.socket || !this.socket.connected) {
      console.warn('[CombatSocketClient] Cannot request effectFrames - not connected');
      return;
    }

    console.log('[CombatSocketClient] Requesting effectFrames for match:', this.matchId);
    this.socket.emit('combat:requestEffectFrames', { matchId: this.matchId });
  }

  /**
   * Setup Socket.IO event handlers
   */
  private setupSocketHandlers(): void {
    if (!this.socket) {
      return;
    }

    // Connection events
    this.socket.on('connect', () => {
      this.updateStatus(ConnectionStatus.CONNECTED);

      // Join match room
      this.socket!.emit('combat:join', this.matchId);
    });

    this.socket.on('connect_error', (error: Error) => {
      this.updateStatus(ConnectionStatus.ERROR);
      this.notifyError(error);
    });

    this.socket.on('disconnect', (reason: string) => {
      // Check if this was a manual disconnect or unexpected
      if (this.manualDisconnect || !this.options.reconnect || !reason) {
        // Manual disconnect, reconnect disabled, or no reason provided
        this.updateStatus(ConnectionStatus.DISCONNECTED);
      } else {
        // Unexpected disconnect with reconnect enabled
        this.updateStatus(ConnectionStatus.RECONNECTING);
      }
    });

    this.socket.on('error', (error: Error) => {
      this.notifyError(error);
    });

    // Combat events (delta-aware decoding matching CombatSocket pattern)
    this.socket.on('combat:state', (broadcast: CombatBroadcast | CombatState) => {
      let state: CombatState | null;
      if (broadcast && typeof broadcast === 'object' && 'type' in broadcast) {
        // Delta-encoded broadcast — decode
        state = this.deltaDecoder.apply(broadcast as CombatBroadcast);
      } else {
        // Legacy full state (no type field)
        state = broadcast as CombatState;
      }
      if (state) {
        this.combatStateCallbacks.forEach(callback => callback(state!));
      }
    });

    this.socket.on('combat:events', (events: CombatEvent[]) => {
      this.combatEventsCallbacks.forEach(callback => callback(events));
    });

    this.socket.on('combat:completed', (result: CombatResult) => {
      this.combatCompletedCallbacks.forEach(callback => callback(result));
    });

    // TASK-EFD-003: EffectFrames reliable delivery handler (with buffering fix)
    this.socket.on('combat:effectFrames', (data: EffectFramesData) => {
      console.log(`[CombatSocketClient] Received effectFrames for ${Object.keys(data.frames).length} units`);

      // TASK-EFD-FIX: Buffer effectFrames for late callback registration
      this.bufferedEffectFrames = data;

      // Forward to all registered callbacks
      if (this.effectFramesCallbacks.length > 0) {
        this.effectFramesCallbacks.forEach(callback => callback(data));
      } else {
        console.log(`[CombatSocketClient] No callbacks registered yet, effectFrames buffered for replay`);
      }
    });
  }

  /**
   * Cleanup Socket.IO event handlers
   */
  private cleanupSocketHandlers(): void {
    if (!this.socket) {
      return;
    }

    // Remove all listeners
    this.socket.off('connect');
    this.socket.off('connect_error');
    this.socket.off('disconnect');
    this.socket.off('error');
    this.socket.off('combat:state');
    this.socket.off('combat:events');
    this.socket.off('combat:completed');
    this.socket.off('combat:effectFrames'); // TASK-EFD-003
  }

  /**
   * Update connection status and notify listeners
   */
  private updateStatus(status: ConnectionStatus): void {
    this.status = status;
    this.statusChangeCallbacks.forEach(callback => callback(status));
  }

  /**
   * Notify error callbacks
   */
  private notifyError(error: Error): void {
    this.errorCallbacks.forEach(callback => callback(error));
  }
}
